import { darken, lighten } from 'color2k';
import { type FormEventHandler, useEffect, useRef, useState } from 'react';
import { AiDisabledAlert } from '@/components/AiDisabledAlert';
import { Collapse } from '@/components/Collapse';
import { FadeInOut } from '@/components/FadeInOut';
import { FilePreview } from '@/components/FilePreview';
import { QuickCreateErrorMessage } from '@/components/QuickCreateErrorMessage';
import { UploadTarget } from '@/components/UploadTarget';
import { ImageDropzone } from '@/components/UploadTarget/Dropzone';
import type { DropzoneOptions } from '@/components/UploadTarget/types';
import { useCollection } from '@/features/events/contexts/CollectionContext/useCollection';
import {
  useMagic,
  useMagicPrompt,
} from '@/features/magic/contexts/MagicContext';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { MotionFlex, type MotionFlexProps } from '@/ui';
import { Button, Flex, Text } from '@/ui';
import { shakeElement } from '@/utils/shakeElement';
import { useJobContext } from '../../contexts/JobContext/useJobContext';
// import { useUnsavedPromptWarning } from '../../hooks/useUnsavedPromptWarning';
import { hasFilePrompt } from '../../utils/prompt';
import { Card } from '../Card';
import { LoadingText } from '../LoadingText';
import { SubmitButton } from './components/SubmitButton';

type Props = Omit<MotionFlexProps, 'onSubmit'> & {
  onSubmit: () => void;
  cta?: (props: DropzoneOptions) => JSX.Element;
};

export const ImagePromptForm = ({ cta, onSubmit, ...props }: Props) => {
  const { state, resetAppState } = useMagic();
  const { prompt, setPrompt, instructions, setInstructions } = useMagicPrompt();
  const { reset: resetCreateJob, createJobError, job } = useJobContext();
  const { base } = useCollection();
  const { value: disableAllAiFeatures } = useFeatureFlag(
    'disableAllAiFeatures'
  );
  const uploadRef = useRef<HTMLDivElement | null>(null);
  const { trackEvent } = useAnalytics();
  const isLoading = state === 'loading';

  const [forceShowSubmitButton, setForceShowSubmitButton] = useState(false);

  useEffect(() => {
    if (!base?.job && !base?.chat) {
      setForceShowSubmitButton(false);
    }
  }, [base]);

  let showSubmitButton = true;
  if (forceShowSubmitButton) {
    showSubmitButton = true;
  } else if (isLoading || base) {
    showSubmitButton = false;
  } else if (!hasFilePrompt(prompt)) {
    showSubmitButton = false;
  }

  const handleSubmit: FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault();
    resetCreateJob();

    if (!prompt.file) {
      await shakeElement(uploadRef);
    } else {
      onSubmit();
    }
  };

  const handleFocus = () => {
    if (base?.job || base?.chat) {
      setForceShowSubmitButton(true);
    }
  };

  const handleClearClick = () => {
    resetAppState();
    setInstructions('');
    trackEvent('quick-create:click again');
  };

  const handleFileSelect = (file: File | null) => {
    setPrompt({ file });

    // force the submit button to appear when selecting a new file while
    // viewing the results of a previous job
    if (base?.job || base?.chat) {
      setForceShowSubmitButton(true);
    }
  };

  // useUnsavedPromptWarning({ file: prompt.file, text: instructions.trim() });

  return (
    <MotionFlex
      as="form"
      direction="column"
      gap="2"
      // @ts-expect-error - onSubmit is a valid prop with `as`
      onSubmit={handleSubmit}
      {...props}
    >
      {disableAllAiFeatures && <AiDisabledAlert />}

      <Collapse in={Boolean(createJobError)}>
        {createJobError && (
          <QuickCreateErrorMessage error={createJobError} type={job?.type} />
        )}
      </Collapse>

      <Flex direction="column" gap="2">
        <FadeInOut key={String(Boolean(state === 'edit' && prompt.file))}>
          {state === 'edit' && prompt.file ? (
            <Card direction="column" gap="4" justify="center" p="5">
              <FilePreview file={prompt.file} />
              {instructions && <Text fontWeight="medium">{instructions}</Text>}
            </Card>
          ) : (
            <UploadTarget
              bg="white"
              borderColor={prompt.file ? 'transparent' : '#c7b5e8'}
              borderRadius="xl"
              file={prompt.file}
              handleClearClick={handleClearClick}
              instructions={instructions}
              isDisabled={disableAllAiFeatures || isLoading}
              isExampleEnabled
              ref={uploadRef}
              w="100%"
              cta={
                cta ||
                (({ isDisabled }) => <ImageDropzone isDisabled={isDisabled} />)
              }
              dragOverProps={{
                bg: lighten('#c7b5e8', 0.1),
                borderColor: darken('#c7b5e8', 0.2),
              }}
              onDropText={() => {}}
              onFocus={handleFocus}
              onInstructionsChange={setInstructions}
              onSelectFile={handleFileSelect}
            />
          )}
        </FadeInOut>

        <Collapse in={showSubmitButton}>
          <SubmitButton
            data-testid="prompt-file-submit"
            isDisabled={isLoading || disableAllAiFeatures}
            textAlign={isLoading ? 'left' : 'center'}
            onClick={() => trackEvent('quick-create:click generate')}
          >
            {isLoading ? (
              <LoadingText w="170px">🔮 Working our magic</LoadingText>
            ) : (
              <span>🪄 Create</span>
            )}
          </SubmitButton>
        </Collapse>

        {state === 'edit' && (
          <Button
            colorScheme="dark"
            fontSize="md"
            isDisabled={isLoading || disableAllAiFeatures}
            mt={{ base: 2, md: 1 }}
            textDecor="underline"
            variant="link"
            onClick={handleClearClick}
          >
            Clear and start over
          </Button>
        )}
      </Flex>
    </MotionFlex>
  );
};
